// ── Adressbasierte „eigene-Wallet-Erkennung" für Transfers ───────────────────
//
// ZIEL: Wenn die Ziel-Adresse einer Auszahlung (`chainTo`) bzw. die Quell-Adresse
// einer Einzahlung (`chainFrom`) EXAKT mit einer vom Kunden unter „Meine Wallets"
// hinterlegten Adresse übereinstimmt, ist die TX nachweislich ein Transfer
// zwischen EIGENEN Wallets — kein „Externe Wallet"-Versand, KEINE steuerpflichtige
// Veräußerung (EStG §23 / INVARIANT 4: own-wallet-Transfer = null Tax-Events).
//
// WASSERDICHT — die einzige Regel: NUR bei kryptografischer Gewissheit (exakter,
// normalisierter Adress-Treffer) wird umklassifiziert. Gibt es keinen Adress-
// Beweis (keine Adresse am TX, keine registrierte Adresse, oder kein Treffer),
// wird NICHTS verändert — lieber offen lassen als falsch zusammenfassen. Heuristik
// (Menge/Zeit) ist hier bewusst ausgeschlossen; die übernimmt weiter der
// transferMatcher, der ungepaarte Transfers konservativ behandelt.
//
// Konsumiert von App.handleImportCsv (einzige Import-Senke) → die Bewertung wird
// einmal beim Import „eingebacken" und fließt danach durch ALLE Konsumenten
// (fifo.js, austrianCost.js, WalletScreen, AssetDetailScreen, portfolioCalc,
// reconciliationEngine), ohne die Registry in jeden Screen fädeln zu müssen.

// Adress-Normalisierung (trim + lowercase) — spiegelt walletRegistry.normalizeAddress,
// bewusst hier inline gehalten, damit dieses tax-relevante Modul SEITENEFFEKTFREI
// bleibt (kein Firebase-Import über den Service) und isoliert testbar ist.
function normalizeAddress(addr) {
  return String(addr || '').trim().toLowerCase();
}

/**
 * Baut einen Index `addressLc → wallet` aus den registrierten Wallets.
 * Adressen werden normalisiert (trim + lowercase), damit Groß-/Kleinschreibung
 * (EVM-Checksum-Adressen!) keinen Treffer verhindert.
 *
 * @param {Array<{label?:string, chain?:string, address?:string, addressLc?:string}>} wallets
 * @returns {Map<string, object>} leer, wenn keine gültigen Adressen vorhanden sind
 */
export function buildAddressIndex(wallets) {
  const idx = new Map();
  for (const w of Array.isArray(wallets) ? wallets : []) {
    const lc = w?.addressLc || normalizeAddress(w?.address);
    if (lc) idx.set(lc, w);
  }
  return idx;
}

/**
 * Prüft eine EINZELNE TX gegen den Adress-Index.
 * Gibt das Match-Objekt zurück oder null (kein Beweis → keine Änderung).
 *
 *   auszahlung + chainTo  ∈ Registry → { wallet, role: 'to' }   (an eigene Wallet)
 *   einzahlung + chainFrom ∈ Registry → { wallet, role: 'from' } (von eigener Wallet)
 *
 * Nur die ON-CHAIN-GEGENADRESSE zählt. Die Absenderadresse einer Auszahlung
 * (Börsen-Hot-Wallet) bzw. die Empfängeradresse einer Einzahlung (eigene Börse)
 * werden bewusst NICHT geprüft — sie beweisen keinen own-wallet-Transfer.
 *
 * @returns {{wallet: object, role: 'to'|'from'}|null}
 */
export function matchOwnWallet(tx, addressIndex) {
  if (!tx || !(addressIndex instanceof Map) || addressIndex.size === 0) return null;
  // Eine bereits vom Nutzer bewusst klassifizierte TX wird NICHT automatisch
  // überschrieben — User-Autorität gewinnt (vgl. userEdited-Schutz im Read-Layer).
  // Beim Import ist userEdited nie gesetzt; der Schutz greift nur, falls die
  // Funktion künftig auf Bestandsdaten (Re-Scan) angewandt wird.
  if (tx.userEdited) return null;

  if (tx.typ === 'auszahlung') {
    const lc = normalizeAddress(tx.chainTo);
    const w = lc && addressIndex.get(lc);
    if (w) return { wallet: w, role: 'to' };
  } else if (tx.typ === 'einzahlung') {
    const lc = normalizeAddress(tx.chainFrom);
    const w = lc && addressIndex.get(lc);
    if (w) return { wallet: w, role: 'from' };
  }
  return null;
}

/**
 * Wendet die own-wallet-Klassifikation auf ein TX-Array an.
 *
 * - Bei exaktem Adress-Treffer: setzt `ownWalletTransfer:true`, `ownWalletLabel`
 *   (Wallet-Name für die Anzeige), `ownWalletRole`, und LÖSCHT `externalTransfer`
 *   (false) — damit FIFO die Lots intakt lässt (own-wallet-Transfer ist KEINE
 *   Veräußerung; das ursprüngliche Anschaffungsdatum bleibt erhalten).
 * - Ohne Treffer bleibt die TX BYTE-IDENTISCH. Wird gar nichts geändert, wird das
 *   Original-Array referenz-gleich zurückgegeben (downstream-Caches bleiben warm).
 *
 * Diese Funktion fügt NIEMALS `externalTransfer:true` hinzu und fasst NIEMALS TXs
 * zusammen — sie annotiert nur einzelne, bewiesene Transfers.
 *
 * @param {Array} txs
 * @param {Array} wallets  registrierte Wallets (listWallets)
 * @returns {Array}
 */
export function classifyOwnWalletTransfers(txs, wallets) {
  if (!Array.isArray(txs) || txs.length === 0) return txs;
  const idx = buildAddressIndex(wallets);
  if (idx.size === 0) return txs; // keine Registry → nichts beweisbar → nichts ändern

  let changed = false;
  const out = txs.map(tx => {
    const m = matchOwnWallet(tx, idx);
    if (!m) return tx;
    changed = true;
    return {
      ...tx,
      ownWalletTransfer: true,
      ownWalletLabel: m.wallet.label || null,
      ownWalletRole: m.role,
      // Bewiesener eigener Transfer ⇒ KEIN externer Abgang. Flag entfernen, damit
      // fifo.js die Lots nicht konsumiert (sonst ginge die Kostenbasis verloren).
      externalTransfer: false,
    };
  });
  return changed ? out : txs;
}
